import { Fragment, useEffect, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Loader2, Palette } from "lucide-react";

const POSICION_LABEL = {
  derecha: "Derecha", izquierda: "Izquierda", delantera: "Delantera", trasera: "Trasera",
  superior: "Superior", inferior: "Inferior", central: "Central", interior: "Interior",
  exterior: "Exterior",
};

// Valores efectivos de una fila: si el usuario ya eligió una variante de color, se usan los
// de esa variante (y "existente" solo se conserva si la variante elegida es la representante
// que vino con el dato); si no, los de la fila tal como llegaron de la IA.
const efectivo = (f) => (f.varianteElegida
  ? { codigo_parte: f.varianteElegida.codigo_parte, color: f.varianteElegida.color,
      nombre: f.varianteElegida.nombre || f.nombre,
      existente: f.varianteElegida.codigo_parte === f.codigo_parte ? f.existente : null }
  : { codigo_parte: f.codigo_parte, color: f.color, nombre: f.nombre, existente: f.existente });

const FilaSugerencia = ({ f, i, puedeCrear, setFila, elegirVariante, esPrimero }) => {
  const ef = efectivo(f);
  const bloqueadaPorVariante = !!f.variantes_color && !f.varianteElegida;
  return (
    <div data-testid={`sugerencias-ia-fila-${i}`}
      className={`rounded-lg border p-3 mb-2 transition-colors ${f.sel ? "border-indigo-200 bg-indigo-50/40" : "border-slate-200"} ${!bloqueadaPorVariante && !ef.existente && !puedeCrear ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-2.5">
        <Checkbox checked={f.sel} onCheckedChange={(v) => setFila(i, "sel", !!v)}
          disabled={bloqueadaPorVariante || (!ef.existente && !puedeCrear)}
          data-testid={`sugerencias-ia-check-${i}`} className="mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-semibold text-slate-700">{ef.codigo_parte}</span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{f.grupo}</span>
            {ef.color && (
              <span data-testid={`sugerencias-ia-color-${i}`}
                className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 border border-amber-200">
                {ef.color}
              </span>
            )}
            {esPrimero && !f.fuera_catalogo && (
              <span data-testid="sugerencias-ia-mas-probable"
                className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-indigo-700">
                Más probable
              </span>
            )}
            {f.conjunto && (
              <span data-testid={`sugerencias-ia-conjunto-${i}`}
                className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-violet-700 border border-violet-200">
                Conjunto — incluye lo pedido
              </span>
            )}
            {f.opcional_conjunto && (
              <span data-testid={`sugerencias-ia-opcional-${i}`}
                className="rounded bg-fuchsia-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-fuchsia-700 border border-fuchsia-200">
                Opcional
              </span>
            )}
            {f.fuente === "sistema" ? (
              <span data-testid={`sugerencias-ia-fuente-${i}`}
                className="rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-teal-700 border border-teal-200">
                En sistema
              </span>
            ) : (
              <span data-testid={`sugerencias-ia-fuente-${i}`}
                className="rounded bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500 border border-slate-200">
                Referencia (manual)
              </span>
            )}
            {f.compatible && (
              <span data-testid={`sugerencias-ia-compatible-${i}`}
                className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-sky-700 border border-sky-200">
                Compatible
              </span>
            )}
            {(f.posiciones || []).map((p) => (
              <span key={p} data-testid={`sugerencias-ia-posicion-${i}-${p}`}
                className="rounded bg-cyan-50 px-1.5 py-0.5 text-[10px] font-semibold text-cyan-700 border border-cyan-200">
                {POSICION_LABEL[p] || p}
              </span>
            ))}
            {f.cantidad > 0 && (
              <span data-testid={`sugerencias-ia-cantidad-${i}`}
                className="rounded bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 border border-slate-200">
                Cant. {f.cantidad}
              </span>
            )}
            {f.compartida && (
              <span data-testid={`sugerencias-ia-compartida-${i}`}
                className="text-[10px] italic text-slate-400">
                compartida del bloque
              </span>
            )}
            {!bloqueadaPorVariante && !ef.existente && !puedeCrear && (
              <span data-testid={`sugerencias-ia-no-creable-${i}`}
                className="rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-600 border border-rose-200">
                No está en Repuestos
              </span>
            )}
            {f.fuera_catalogo && (
              <span data-testid={`sugerencias-ia-fuera-catalogo-${i}`}
                className="rounded bg-orange-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-orange-600 border border-orange-200">
                No está en el catálogo de este modelo
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-slate-700">
            {ef.nombre}
            {f.nombre_original && f.nombre_original !== ef.nombre && (
              <span className="ml-1.5 text-xs italic text-slate-400">({f.nombre_original})</span>
            )}
          </p>
          {(f.modelos_compatibles?.length > 0 || f.modelo_texto) && (
            <p className="mt-0.5 text-[11px] text-slate-400" data-testid={`sugerencias-ia-modelos-${i}`}>
              Compatible: {f.modelos_compatibles?.length ? f.modelos_compatibles.join(", ") : f.modelo_texto}
            </p>
          )}
          {f.razon && <p className="mt-0.5 text-xs text-slate-400">{f.razon}</p>}
          {f.variantes_color && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`sugerencias-ia-variantes-${i}`}>
              <Palette size={13} className="text-slate-400" />
              <span className="text-[11px] text-slate-400">Elija el color:</span>
              {f.variantes_color.map((v) => (
                <button key={v.codigo_parte} type="button"
                  data-testid={`sugerencias-ia-variante-color-${i}-${v.color}`}
                  onClick={() => elegirVariante(i, v)}
                  className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${f.varianteElegida?.codigo_parte === v.codigo_parte ? "border-indigo-400 bg-indigo-100 text-indigo-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                  {v.color}
                </button>
              ))}
            </div>
          )}
          {!ef.existente && puedeCrear && f.sel && (
            <div className="mt-1.5">
              <Input data-testid={`sugerencias-ia-nombre-${i}`} value={f.nombreEs}
                onChange={(e) => setFila(i, "nombreEs", e.target.value)}
                placeholder="Nombre en español para el catálogo de trabajo" className="h-8 text-sm" />
            </div>
          )}
          {ef.existente && <p className="mt-1 text-xs text-teal-600">Se agregará el repuesto existente: {ef.existente.nombre}</p>}
        </div>
      </div>
    </div>
  );
};

export const SugerenciasIADialog = ({ open, onOpenChange, modeloId, modeloNombre, pedidoInicial,
  codigoColor = "", usarCantidad = false, onRepuestoListo }) => {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("repuestos.crear");
  const [pedido, setPedido] = useState("");
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [filas, setFilas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [filtroColor, setFiltroColor] = useState("");
  const [filtroModelo, setFiltroModelo] = useState("");
  const [filtroPosicion, setFiltroPosicion] = useState("");

  const consultar = async (texto) => {
    if (!texto.trim()) return;
    setCargando(true); setResultado(null); setFilas([]);
    setFiltroColor(""); setFiltroModelo(""); setFiltroPosicion("");
    try {
      const { data } = await api.post("/solicitud/sugerencias-ia",
        { modelo_id: modeloId, pedido: texto, codigo_color: codigoColor || "" });
      setResultado(data);
      const sinOpcionales = data.sugerencias.filter((s) => !s.opcional_conjunto);
      const primeroPorItem = {};
      for (const s of sinOpcionales) {
        if (!(s.item in primeroPorItem)) primeroPorItem[s.item] = s;
      }
      setFilas(data.sugerencias.map((s) => ({ ...s,
        varianteElegida: null,
        sel: !s.variantes_color && !s.opcional_conjunto && !s.fuera_catalogo &&
             (!!s.conjunto || primeroPorItem[s.item] === s) &&
             (!!s.existente || puedeCrear),
        nombreEs: s.existente ? s.existente.nombre : s.nombre })));
    } catch (e) { toast.error(errorMsg(e)); setResultado({ sugerencias: [], nota: "" }); }
    finally { setCargando(false); }
  };

  useEffect(() => {
    if (!open) { setResultado(null); setFilas([]); return; }
    setPedido(pedidoInicial || "");
    if (pedidoInicial?.trim()) consultar(pedidoInicial);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFila = (i, campo, valor) =>
    setFilas((prev) => prev.map((f, x) => (x === i ? { ...f, [campo]: valor } : f)));
  const elegirVariante = (i, variante) =>
    setFilas((prev) => prev.map((f, x) => {
      if (x !== i) return f;
      const yaElegida = f.varianteElegida?.codigo_parte === variante.codigo_parte;
      if (yaElegida) return { ...f, sel: false, varianteElegida: null };
      const existenteEfectivo = variante.codigo_parte === f.codigo_parte ? f.existente : null;
      return { ...f, sel: true, varianteElegida: variante,
              nombreEs: existenteEfectivo ? existenteEfectivo.nombre : (variante.nombre || f.nombre) };
    }));
  const marcarItem = (item, valor) =>
    setFilas((prev) => prev.map((f) => {
      if (f.item !== item || f.opcional_conjunto || f.variantes_color) return f;
      if (valor && !f.existente && !puedeCrear) return f; // no marcar piezas que no se pueden crear
      return { ...f, sel: valor };
    }));

  const seleccionadas = filas.filter((f) => f.sel);
  const noCreables = filas.filter((f) => !efectivo(f).existente && !puedeCrear).length;
  const colores = [...new Set(filas.map((f) => efectivo(f).color).filter(Boolean))];
  const modelosTxt = [...new Set(filas.map((f) => f.modelo_texto).filter(Boolean))];
  const posicionesDisponibles = [...new Set(filas.flatMap((f) => f.posiciones || []))];
  const filasPrincipales = filas.map((f, i) => ({ f, i })).filter(({ f }) => !f.opcional_conjunto);
  const filasOpcionales = filas.map((f, i) => ({ f, i })).filter(({ f }) => f.opcional_conjunto);
  const multiItem = new Set(filasPrincipales.map(({ f }) => f.item || "")).size > 1;
  const primeroDelItem = (f, idxEnPrincipales) =>
    filasPrincipales.findIndex(({ f: x }) => (x.item || "") === (f.item || "")) === idxEnPrincipales;
  const itemTieneConjunto = (item) =>
    filasPrincipales.some(({ f: x }) => (x.item || "") === item && x.conjunto);
  const visible = (f) =>
    (!filtroColor || efectivo(f).color === filtroColor) && (!filtroModelo || f.modelo_texto === filtroModelo) &&
    (!filtroPosicion || (f.posiciones || []).includes(filtroPosicion));

  const confirmar = async () => {
    setGuardando(true);
    let agregados = 0;
    try {
      for (const f of seleccionadas) {
        const ef = efectivo(f);
        let repFinal = ef.existente;
        if (!repFinal) {
          const { data } = await api.post("/catalogo-referencia/crear-repuesto", {
            codigo_parte: ef.codigo_parte, nombre: f.nombreEs.trim() || ef.nombre, modelo_ids: [modeloId],
            color: ef.color || "",
          });
          repFinal = data.repuesto;
          if (data.existente) toast.info(`${ef.codigo_parte} ya existía; se usó el repuesto existente`);
        }
        if (usarCantidad) onRepuestoListo(repFinal, f.cantidad || 1);
        else onRepuestoListo(repFinal);
        agregados++;
      }
      toast.success(`${agregados} repuesto${agregados !== 1 ? "s" : ""} agregado${agregados !== 1 ? "s" : ""}`);
      onOpenChange(false);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl max-h-[88vh] flex flex-col p-4 sm:p-6" data-testid="sugerencias-ia-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <Sparkles size={18} className="text-indigo-600" /> Sugerencias de IA — {modeloNombre}
            <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-600">Experimental</span>
          </DialogTitle>
        </DialogHeader>

        <div className="shrink-0 flex gap-2">
          <Input data-testid="sugerencias-ia-pedido-input" value={pedido}
            onChange={(e) => setPedido(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !cargando && consultar(pedido)}
            placeholder='Describa el repuesto en español (ej: "roles de aro delantero", "descansapié izquierdo completo")' />
          <Button data-testid="sugerencias-ia-consultar-btn" onClick={() => consultar(pedido)}
            disabled={cargando || !pedido.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0">
            {cargando ? <Loader2 size={15} className="animate-spin" /> : "Sugerir"}
          </Button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain mt-3">
          {cargando && (
            <div className="flex items-center gap-2 py-8 justify-center text-sm text-slate-500" data-testid="sugerencias-ia-cargando">
              <Loader2 size={16} className="animate-spin" /> La IA está buscando en el catálogo de referencia...
            </div>
          )}
          {!cargando && resultado && (
            <>
              {resultado.nota && (
                <p className="mb-2 rounded-md bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-slate-600"
                  data-testid="sugerencias-ia-nota">{resultado.nota}</p>
              )}
              {noCreables > 0 && (
                <p className="mb-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700"
                  data-testid="sugerencias-ia-aviso-permiso">
                  Hay {noCreables} pieza{noCreables !== 1 ? "s" : ""} que no está{noCreables !== 1 ? "n" : ""} en el
                  catálogo de Repuestos. Pida a quien administra el catálogo que las cree para poder agregarlas.
                </p>
              )}
              {filas.length === 0 && (
                <p className="py-6 text-center text-sm text-slate-400" data-testid="sugerencias-ia-vacio">
                  Sin sugerencias. Pruebe describirlo con otras palabras o use la creación manual del catálogo.
                </p>
              )}
              {filas.length > 0 && (
                <p className="mb-2 text-xs text-slate-400" data-testid="sugerencias-ia-instruccion">
                  {filas.some((f) => f.conjunto)
                    ? "Las piezas del conjunto vienen pre-marcadas; desmarque las que no necesite."
                    : "Marque la(s) pieza(s) correcta(s) — ninguna viene seleccionada."}
                </p>
              )}
              {(colores.length > 1 || modelosTxt.length > 1 || posicionesDisponibles.length > 1) && (
                <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="sugerencias-ia-filtros">
                  {modelosTxt.length > 1 && (
                    <>
                      <span className="text-[10px] font-semibold uppercase text-slate-400">Modelo:</span>
                      {modelosTxt.map((m) => (
                        <button key={m} type="button" data-testid={`sugerencias-ia-filtro-modelo-${m}`}
                          onClick={() => setFiltroModelo(filtroModelo === m ? "" : m)}
                          className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${filtroModelo === m ? "border-indigo-400 bg-indigo-100 text-indigo-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                          {m}
                        </button>
                      ))}
                    </>
                  )}
                  {colores.length > 1 && (
                    <>
                      <span className="ml-1 text-[10px] font-semibold uppercase text-slate-400">Color:</span>
                      {colores.map((c) => (
                        <button key={c} type="button" data-testid={`sugerencias-ia-filtro-color-${c}`}
                          onClick={() => setFiltroColor(filtroColor === c ? "" : c)}
                          className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${filtroColor === c ? "border-indigo-400 bg-indigo-100 text-indigo-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                          {c}
                        </button>
                      ))}
                    </>
                  )}
                  {posicionesDisponibles.length > 1 && (
                    <>
                      <span className="ml-1 text-[10px] font-semibold uppercase text-slate-400">Posición:</span>
                      {posicionesDisponibles.map((p) => (
                        <button key={p} type="button" data-testid={`sugerencias-ia-filtro-posicion-${p}`}
                          onClick={() => setFiltroPosicion(filtroPosicion === p ? "" : p)}
                          className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${filtroPosicion === p ? "border-indigo-400 bg-indigo-100 text-indigo-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                          {POSICION_LABEL[p] || p}
                        </button>
                      ))}
                    </>
                  )}
                  {(filtroColor || filtroModelo || filtroPosicion) && (
                    <button type="button" data-testid="sugerencias-ia-filtro-limpiar"
                      onClick={() => { setFiltroColor(""); setFiltroModelo(""); setFiltroPosicion(""); }}
                      className="text-[11px] text-indigo-600 underline">limpiar</button>
                  )}
                </div>
              )}
              {filasPrincipales.map(({ f, i }, idx) => visible(f) && (
                <Fragment key={`${f.codigo_parte}-${i}`}>
                  {primeroDelItem(f, idx) && (multiItem || itemTieneConjunto(f.item || "")) && (
                    <div className="mt-3 mb-1.5 flex items-center justify-between border-b border-indigo-100 pb-1">
                      {multiItem ? (
                        <p data-testid={`sugerencias-ia-item-header-${i}`}
                          className="text-[11px] font-bold uppercase tracking-wide text-indigo-500">
                          {f.item}
                        </p>
                      ) : <span />}
                      {itemTieneConjunto(f.item || "") && (
                        <div className="flex gap-2 shrink-0">
                          <button type="button" data-testid={`sugerencias-ia-marcar-todas-${i}`}
                            onClick={() => marcarItem(f.item, true)}
                            className="text-[10px] font-semibold text-indigo-600 underline">Marcar todas</button>
                          <button type="button" data-testid={`sugerencias-ia-desmarcar-todas-${i}`}
                            onClick={() => marcarItem(f.item, false)}
                            className="text-[10px] font-semibold text-slate-400 underline">Desmarcar todas</button>
                        </div>
                      )}
                    </div>
                  )}
                  <FilaSugerencia f={f} i={i} puedeCrear={puedeCrear} setFila={setFila}
                    elegirVariante={elegirVariante} esPrimero={primeroDelItem(f, idx)} />
                </Fragment>
              ))}
              {filasOpcionales.length > 0 && (
                <div className="mt-4">
                  <p data-testid="sugerencias-ia-opcionales-header"
                    className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-fuchsia-500 border-b border-fuchsia-100 pb-1">
                    Tornillería y gomas del conjunto (opcionales)
                  </p>
                  {filasOpcionales.map(({ f, i }) => visible(f) && (
                    <FilaSugerencia key={`${f.codigo_parte}-${i}`} f={f} i={i} puedeCrear={puedeCrear}
                      setFila={setFila} elegirVariante={elegirVariante} />
                  ))}
                </div>
              )}
            </>
          )}
          {!cargando && !resultado && (
            <p className="py-6 text-center text-sm text-slate-400">
              Describa el repuesto en español: la IA busca primero en los repuestos ya ingresados al sistema (con sus equivalentes) y luego en el catálogo de referencia Honda.
            </p>
          )}
        </div>

        <div className="shrink-0 flex justify-end gap-2 pt-3 border-t border-slate-100">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="sugerencias-ia-confirmar-btn" onClick={confirmar}
            disabled={guardando || seleccionadas.length === 0 || seleccionadas.some((f) => !efectivo(f).existente && !f.nombreEs.trim())}
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {guardando ? "Agregando..." : `Crear y agregar (${seleccionadas.length})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
